/** @module SearchModal */

/** Class representing search modal functionality */
export default class SearchModal {
  /** Initialize the search modal functionality */
  constructor() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  /** Initialize the component */
  init() {
    this.openBtn = document.querySelector('.search-bar__button');
    this.modal = document.querySelector('.search-modal');
    this.closeBtn = document.querySelector('.search-modal__close');
    this.searchInput = document.querySelector('.search-modal__input');
    this.searchForm = document.querySelector('.search-modal__form');

    if (!this.openBtn || !this.modal) {
      return;
    }

    // Store focusable elements for tab trap
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    // Store the element that had focus before modal opened
    this.previousActiveElement = null;

    // Make modal elements non-focusable initially
    this.makeModalElementsNonFocusable();

    this.bindEvents();
  }

  /** Get all focusable elements within the modal */
  getFocusableElements() {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ];

    return Array.from(
      this.modal.querySelectorAll(focusableSelectors.join(', '))
    );
  }

  /** Make modal elements non-focusable when modal is closed */
  makeModalElementsNonFocusable() {
    const focusableElements = this.getFocusableElements();
    focusableElements.forEach((element) => {
      // Store original tabindex if it exists
      if (!element.hasAttribute('data-original-tabindex')) {
        const originalTabIndex = element.getAttribute('tabindex');
        if (originalTabIndex !== null) {
          element.setAttribute('data-original-tabindex', originalTabIndex);
        }
      }
      // Set tabindex to -1 to make it non-focusable
      element.setAttribute('tabindex', '-1');
    });
  }

  /** Make modal elements focusable when modal is open */
  makeModalElementsFocusable() {
    const focusableElements = this.getFocusableElements();
    focusableElements.forEach((element) => {
      // Restore original tabindex if it existed
      if (element.hasAttribute('data-original-tabindex')) {
        const originalTabIndex = element.getAttribute('data-original-tabindex');
        element.setAttribute('tabindex', originalTabIndex);
        element.removeAttribute('data-original-tabindex');
      } else {
        // Remove tabindex attribute to restore natural focusability
        element.removeAttribute('tabindex');
      }
    });
  }

  /** Bind event listeners for opening/closing modal */
  bindEvents() {
    // Open modal on click
    this.openBtn.addEventListener('click', () => this.openModal());

    // Open modal on Enter key
    this.openBtn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openModal();
      }
    });

    // Close modal (if close button exists)
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeModal());
      this.closeBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.closeModal();
        }
      });
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-active')) {
        this.closeModal();
      }
    });

    // Tab trap within modal
    this.modal.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && this.modal.classList.contains('is-active')) {
        this.handleTabKey(e);
      }
    });

    // Close modal when clicking outside
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Handle form submission
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', () => {
        // Allow form submission but close modal after
        setTimeout(() => {
          this.closeModal();
        }, 100);
      });
    }
  }

  /**
   * Handle tab key navigation within modal
   * @param e
   */
  handleTabKey(e) {
    if (this.focusableElements.length === 0) return;

    if (e.shiftKey) {
      // Shift + Tab: move backwards
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else if (document.activeElement === this.lastFocusableElement) {
      // Tab: move forwards
      e.preventDefault();
      this.firstFocusableElement.focus();
    }
  }

  /** Open modal */
  openModal() {
    // Store the currently focused element
    this.previousActiveElement = document.activeElement;

    // Update ARIA attributes
    this.openBtn.setAttribute('aria-expanded', 'true');
    this.modal.setAttribute('aria-hidden', 'false');

    // Show modal
    this.modal.classList.add('is-active');

    // Make modal elements focusable again
    this.makeModalElementsFocusable();

    // Get focusable elements
    this.focusableElements = this.getFocusableElements();
    [this.firstFocusableElement] = this.focusableElements;
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1];

    // Focus on search input (or first focusable element if input doesn't exist)
    const elementToFocus = this.searchInput || this.firstFocusableElement;
    if (elementToFocus) {
      // Use setTimeout to ensure modal is visible before focusing
      setTimeout(() => {
        elementToFocus.focus();
        // Select all text if it's an input
        if (
          elementToFocus.tagName === 'INPUT' &&
          elementToFocus.type === 'search'
        ) {
          elementToFocus.select();
        }
      }, 10);
    }

    // Announce modal opening to screen readers
    SearchModal.announceToScreenReader(
      'Search modal opened. Use Tab to navigate and Escape to close.'
    );
  }

  /** Close modal */
  closeModal() {
    // Update ARIA attributes
    this.openBtn.setAttribute('aria-expanded', 'false');
    this.modal.setAttribute('aria-hidden', 'true');

    // Hide modal
    this.modal.classList.remove('is-active');

    // Make modal elements non-focusable
    this.makeModalElementsNonFocusable();

    // Return focus to the element that had focus before modal opened
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }

    // Clear stored elements
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.previousActiveElement = null;

    // Announce modal closing to screen readers
    SearchModal.announceToScreenReader('Search modal closed.');
  }

  /**
   * Announce message to screen readers
   * @param message
   */
  static announceToScreenReader(message) {
    // Create or get existing announcement element
    let announcement = document.getElementById('sr-announcement');
    if (!announcement) {
      announcement = document.createElement('div');
      announcement.id = 'sr-announcement';
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.style.position = 'absolute';
      announcement.style.left = '-10000px';
      announcement.style.width = '1px';
      announcement.style.height = '1px';
      announcement.style.overflow = 'hidden';
      document.body.appendChild(announcement);
    }

    announcement.textContent = message;
  }

  /** Toggle modal */
  toggleModal() {
    if (this.modal.classList.contains('is-active')) {
      this.closeModal();
    } else {
      this.openModal();
    }
  }
}

// Initialize when loaded as a module
(() => new SearchModal())();
